/**
 * Shared pointer drag controller for stacked slide carousels.
 * Momentum projection, rubber-band edges, and eased snap animations.
 */
(function () {
  const EASE_OUT_EXPO = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

  class CarouselDrag {
    constructor(options) {
      this.surface = options.surface;
      this.slides = options.slides;
      this.wrap = options.wrap ?? false;
      this.getIndex = options.getIndex;
      this.onSnapComplete = options.onSnapComplete;
      this.onDragStart = options.onDragStart ?? (() => {});
      this.onDragEnd = options.onDragEnd ?? (() => {});
      this.clickSuppressSelector = options.clickSuppressSelector ?? null;
      this.draggableClass = options.draggableClass ?? '';
      this.draggingClass = options.draggingClass ?? '';

      this.swipeThreshold = 0.18;
      this.momentumFactor = 320;
      this.velocityMin = 0.42;
      this.velocitySamples = [];
      this.dragState = null;
      this.animFrame = null;
      this.suppressClick = false;
      this.active = false;

      if (!this.surface || !this.slides || this.slides.length <= 1) return;

      this.active = true;
      this.bind();

      if (this.draggableClass) {
        this.surface.classList.add(this.draggableClass);
      }
    }

    bind() {
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerEnd = this.handlePointerEnd.bind(this);
      this.onClickCapture = this.handleClickCapture.bind(this);

      this.surface.addEventListener('pointerdown', this.onPointerDown);
      this.surface.addEventListener('pointermove', this.onPointerMove);
      this.surface.addEventListener('pointerup', this.onPointerEnd);
      this.surface.addEventListener('pointercancel', this.onPointerEnd);

      if (this.clickSuppressSelector) {
        this.surface.addEventListener('click', this.onClickCapture, true);
      }
    }

    destroy() {
      if (!this.active) return;

      this.cancelAnimation();
      this.surface.removeEventListener('pointerdown', this.onPointerDown);
      this.surface.removeEventListener('pointermove', this.onPointerMove);
      this.surface.removeEventListener('pointerup', this.onPointerEnd);
      this.surface.removeEventListener('pointercancel', this.onPointerEnd);

      if (this.clickSuppressSelector) {
        this.surface.removeEventListener('click', this.onClickCapture, true);
      }

      this.surface.classList.remove(this.draggableClass, this.draggingClass);
      this.active = false;
    }

    cancelAnimation() {
      if (this.animFrame) {
        cancelAnimationFrame(this.animFrame);
        this.animFrame = null;
      }
    }

    getAdjacentIndices(currentIndex) {
      const length = this.slides.length;

      if (this.wrap) {
        return {
          next: (currentIndex + 1) % length,
          prev: (currentIndex - 1 + length) % length,
        };
      }

      return {
        next: currentIndex < length - 1 ? currentIndex + 1 : null,
        prev: currentIndex > 0 ? currentIndex - 1 : null,
      };
    }

    trackVelocity(x) {
      const now = performance.now();
      this.velocitySamples.push({ x, t: now });
      this.velocitySamples = this.velocitySamples.filter((sample) => now - sample.t < 90);
    }

    getVelocity() {
      const samples = this.velocitySamples;

      if (samples.length < 2) return 0;

      const first = samples[0];
      const last = samples[samples.length - 1];
      const dt = last.t - first.t;

      return dt > 0 ? (last.x - first.x) / dt : 0;
    }

    applyResistance(deltaX) {
      if (this.wrap) return deltaX;

      const index = this.getIndex();
      const { next, prev } = this.getAdjacentIndices(index);

      if (prev === null && deltaX > 0) return deltaX * 0.32;
      if (next === null && deltaX < 0) return deltaX * 0.32;

      return deltaX;
    }

    updateDrag(deltaX) {
      const width = this.surface.offsetWidth;
      const resisted = this.applyResistance(deltaX);
      const currentIndex = this.getIndex();
      const activeSlide = this.slides[currentIndex];
      const { next, prev } = this.getAdjacentIndices(currentIndex);

      activeSlide.style.transform = `translate3d(${resisted}px, 0, 0)`;
      activeSlide.style.opacity = '1';
      activeSlide.style.zIndex = '2';

      this.slides.forEach((slide, index) => {
        if (index === currentIndex) return;

        slide.style.transition = 'none';
        slide.style.opacity = '0';
        slide.style.transform = '';
        slide.style.zIndex = '0';
      });

      const progress = 1 - Math.pow(1 - Math.min(Math.abs(resisted) / width, 1), 1.6);

      if (resisted < 0 && next !== null) {
        const nextSlide = this.slides[next];
        nextSlide.style.opacity = String(0.3 + progress * 0.7);
        nextSlide.style.zIndex = '1';
        nextSlide.style.transform = `translate3d(${resisted + width}px, 0, 0)`;
      }

      if (resisted > 0 && prev !== null) {
        const prevSlide = this.slides[prev];
        prevSlide.style.opacity = String(0.3 + progress * 0.7);
        prevSlide.style.zIndex = '1';
        prevSlide.style.transform = `translate3d(${resisted - width}px, 0, 0)`;
      }
    }

    clearDragStyles() {
      this.slides.forEach((slide) => {
        slide.style.transition = '';
        slide.style.transform = '';
        slide.style.opacity = '';
        slide.style.zIndex = '';
      });
    }

    resolveSnapTarget(deltaX, velocity) {
      const width = this.surface.offsetWidth;
      const projected = deltaX + velocity * this.momentumFactor;
      const threshold = width * this.swipeThreshold;
      const currentIndex = this.getIndex();
      const { next, prev } = this.getAdjacentIndices(currentIndex);

      if ((projected < -threshold || velocity < -this.velocityMin) && next !== null) {
        return next;
      }

      if ((projected > threshold || velocity > this.velocityMin) && prev !== null) {
        return prev;
      }

      return currentIndex;
    }

    animatePosition(fromX, toX, onComplete, options = {}) {
      this.cancelAnimation();

      const start = performance.now();
      const distance = Math.abs(toX - fromX);
      const minDuration = options.minDuration ?? 260;
      const maxDuration = options.maxDuration ?? 520;
      const duration = Math.min(maxDuration, Math.max(minDuration, distance * 0.45));

      const tick = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = EASE_OUT_EXPO(progress);
        const x = fromX + (toX - fromX) * eased;

        this.updateDrag(x);

        if (progress < 1) {
          this.animFrame = requestAnimationFrame(tick);
          return;
        }

        this.animFrame = null;
        onComplete();
      };

      this.animFrame = requestAnimationFrame(tick);
    }

    finishDrag(deltaX, velocity) {
      const currentIndex = this.getIndex();
      const targetIndex = this.resolveSnapTarget(deltaX, velocity);
      const width = this.surface.offsetWidth;

      if (targetIndex === currentIndex) {
        this.animatePosition(deltaX, 0, () => {
          this.clearDragStyles();
          this.onDragEnd();
        });
        return;
      }

      const isGoingNext = deltaX < 0 || (Math.abs(deltaX) < 12 && velocity < 0);
      const endX = isGoingNext ? -width : width;

      this.animatePosition(deltaX, endX, () => {
        this.clearDragStyles();
        this.onSnapComplete(targetIndex);
        this.onDragEnd();
      });
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('[data-dot], .mooncake-hero__pagination, .product-hero__pagination')) return;
      if (event.target.closest('a, button')) return;

      this.cancelAnimation();
      this.velocitySamples = [];
      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        currentX: event.clientX,
      };

      this.surface.setPointerCapture(event.pointerId);
      this.surface.classList.add(this.draggingClass);

      const activeSlide = this.slides[this.getIndex()];
      activeSlide.style.transition = 'none';

      this.onDragStart();
    }

    handlePointerMove(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      this.dragState.currentX = event.clientX;
      const deltaX = this.dragState.currentX - this.dragState.startX;

      this.trackVelocity(event.clientX);

      if (Math.abs(deltaX) > 6) {
        event.preventDefault();
      }

      this.updateDrag(deltaX);
    }

    handlePointerEnd(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      const deltaX = this.dragState.currentX - this.dragState.startX;
      const velocity = this.getVelocity();

      if (this.surface.hasPointerCapture(event.pointerId)) {
        this.surface.releasePointerCapture(event.pointerId);
      }

      this.surface.classList.remove(this.draggingClass);

      if (Math.abs(deltaX) > 10) {
        this.suppressClick = true;
        window.setTimeout(() => {
          this.suppressClick = false;
        }, 320);
      }

      this.finishDrag(deltaX, velocity);
      this.dragState = null;
      this.velocitySamples = [];
    }

    handleClickCapture(event) {
      if (!this.suppressClick || !this.clickSuppressSelector) return;

      if (event.target.closest(this.clickSuppressSelector)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  window.CarouselDrag = CarouselDrag;
})();
